import { HttpClient } from '@angular/common/http';
import {
    AfterViewInit,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DataTableDirective } from 'angular-datatables';
import { DialogService } from 'src/app/services/dialog.service';
import { environment } from 'src/environments/environment';
import { DataTablesResponse } from 'src/app/interfaces/data-table-response.interface';
import { helper } from 'src/environments/helper';
import { EntitatiService } from './entitati.service';
import { finalize, Subject } from 'rxjs';
import { ViewEntitateComponent } from './view-entitate/view-entitate.component';
import { FormControl, FormGroup } from '@angular/forms';
import { Generic } from 'src/app/interfaces/generic.interface';
import { PersonsEntityService } from 'src/app/platform/nomenclatoare/persons-entity-types/persons-entity.service';
import { PermissionsService } from 'src/app/services/permissions.service';

@Component({
    selector: 'app-entitati',
    templateUrl: './entitati.component.html',
    styleUrls: ['./entitati.component.scss'],
})
export class EntitatiComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;
    dtTrigger: Subject<any> = new Subject<any>();

    isLoading = false;
    isFilterLoading = false;
    showCard = false;

    helper = helper;

    dtOptions: any = {};
    dtInstance!: DataTables.Api;

    tipuriPersoane!: Generic[];

    dtLink =
        environment.interop.basePath +
        environment.interop.indoc.entitati.getEntitatiDT;

    searchParamsForm!: FormGroup;

    //Form Initial Values
    initialFormValues = {
        denumire: '',
        cnp_cui: '',
        tip_persoana: -1,
    };

    resetForm() {
        this.searchParamsForm.reset(this.initialFormValues);

        this.rerenderDT();
        this.showCard = true;
    }

    applyFilters(): void {
        this.rerenderDT(true);
    }

    buildSearchParamsForm() {
        this.searchParamsForm = new FormGroup({
            denumire: new FormControl(''),
            cnp_cui: new FormControl(''),
            tip_persoana: new FormControl(-1),
        });
    }

    toggleCard() {
        this.showCard = !this.showCard;
    }

    constructor(
        private http: HttpClient,
        private dialog: MatDialog,
        private dialogService: DialogService,
        private entitatiService: EntitatiService,
        private tipuriPersoaneService: PersonsEntityService,
        private permissionsService: PermissionsService
    ) { }

    ngOnInit(): void {
        this.renderDT();
        this.buildSearchParamsForm();
        this.loadTipuriPersoane();
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();
    }

    ngAfterViewInit(): void {
        this.dtTrigger.next('');
    }

    renderDT() {
        this.dtOptions = {
            order: [[0, 'desc']],
            responsive: true,
            serverSide: true,
            searching: false,
            pageLength: 25,
            ajax: (dataTablesParameters: any, callback: any) => {
                this.isLoading = true;

                Object.assign(dataTablesParameters, this.searchParamsForm.value);

                this.http
                    .post<DataTablesResponse>(this.dtLink, dataTablesParameters)
                    .subscribe((resp: any) => {
                        this.isLoading = false;
                        callback({
                            recordsTotal: resp.data.recordsTotal,
                            recordsFiltered: resp.data.recordsFiltered,
                            data: resp.data.data,
                        });
                    });
            },
            language: environment.config.romanianLanguageDataTable,
            columns: [
                {
                    title: 'ID',
                    data: 'id',
                },
                {
                    title: 'Denumire',
                    data: 'denumire',
                    orderable: false,
                },
                {
                    title: 'Persoană contact',
                    data: 'pers_contact',
                    orderable: false,
                },
                {
                    title: 'Tip Persoană',
                    data: 'tip_adresa',
                    orderable: false,
                },
                {
                    title: 'CNP/CUI',
                    data: 'cnp_cui',
                    orderable: false,
                },
                {
                    title: 'Observații',
                    data: 'observatii',
                    orderable: false,
                },
                {
                    title: 'Județ',
                    data: 'judet',
                    orderable: false,
                },
                {
                    title: 'Acțiuni',
                    data: null,
                    orderable: false,
                    render: (data: any, type: any, row: any, met: any) => {
                        return `
                            <div class="d-flex align-items-center gap-1">
                                <button class="action-btn view" title="Vizualizare entitate">
                                    <img class="btn-img" src="../../../../assets/icons/view.svg" alt="view"/>
                                </button>  
                            </div>
                        `;
                    },
                },
            ],
            rowCallback: (row: Node, data: any[] | any) => {
                const lastTd = (row as HTMLElement).querySelector('td:last-child');
                const viewIcon = lastTd?.querySelector('.view') as HTMLElement;

                if (viewIcon) {
                    if (!this.permissionsService.hasPermission('indoc-module.beneficiari.view')) {
                        viewIcon.style.display = 'none';
                    }

                    viewIcon.addEventListener('click', () => {
                        this.openViewDialog(data);
                    });
                }
            },
        };
    }

    openViewDialog(data: any): void {
        this.isLoading = true;

        this.entitatiService.findEntitate(data.id)
            .pipe(finalize(() => this.isLoading = false))
            .subscribe({
                next: (data: any) => {
                    const rowData = data.data.entity;

                    const dialogRef = this.dialog.open(ViewEntitateComponent, {
                        width: '90%',
                        data: rowData,
                    });
                    dialogRef.afterClosed().subscribe();
                },
                error: (error) => {
                    console.error('Error in find entitate: ', error);
                }
            });
    }

    rerenderDT(paginate: boolean = false): void {
        this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
            dtInstance.draw(paginate);
        });

        this.showCard = false;
    }

    loadTipuriPersoane() {
        this.isFilterLoading = true;
        this.tipuriPersoaneService.getListActive()
            .pipe(
                finalize(() => this.isFilterLoading = false)
            )
            .subscribe({
                next: (results: any) => {
                    this.tipuriPersoane = results.data;
                },
                error: (error) => {
                    console.error('Error in get tipuri persoane: ', error);
                }
            })
    }
}
